import React, { useState, useEffect } from 'react';
import { db, getSecondaryAuth } from '../firebase';
import { collection, getDocs, doc, updateDoc, setDoc } from 'firebase/firestore';
import { createUserWithEmailAndPassword, signOut } from 'firebase/auth';
import { useAuth } from '../contexts/AuthContext';
import { Navigate } from 'react-router-dom';
import '../styles/UserRoleManagement.css';

const UserRoleManagement = () => {
  const { userRole } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [message, setMessage] = useState({ text: '', type: '' });
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newUserData, setNewUserData] = useState({
    email: '',
    password: '',
    role: 'customer'
  });
  const [creating, setCreating] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const usersSnapshot = await getDocs(collection(db, 'users'));
      const usersList = usersSnapshot.docs.map(doc => ({
        id: doc.id,
        email: doc.data().email || 'No email',
        role: doc.data().role || 'customer',
        createdAt: doc.data().createdAt || null
      }));
      setUsers(usersList);
    } catch (error) {
      console.error('Error fetching users:', error);
      showMessage('Error loading users', 'error');
    } finally {
      setLoading(false);
    }
  };

  // Email validation
  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  // Password validation
  const validatePassword = (password) => {
    return password.length >= 6;
  };

  // Validate form
  const validateForm = () => {
    const errors = {};

    if (!newUserData.email.trim()) {
      errors.email = 'Email is required';
    } else if (!validateEmail(newUserData.email)) {
      errors.email = 'Invalid email format';
    }

    if (!newUserData.password.trim()) {
      errors.password = 'Password is required';
    } else if (!validatePassword(newUserData.password)) {
      errors.password = 'Password must be at least 6 characters';
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Create new user
  const createNewUser = async () => {
    // Validate form
    if (!validateForm()) {
      return;
    }

    try {
      setCreating(true);

      // Get secondary auth instance
      const secondaryAuth = getSecondaryAuth();

      // Create user in Firebase Auth
      const userCredential = await createUserWithEmailAndPassword(
        secondaryAuth,
        newUserData.email.trim(),
        newUserData.password
      );

      const newUser = userCredential.user;

      // Create user document in Firestore
      await setDoc(doc(db, 'users', newUser.uid), {
        email: newUserData.email.trim(),
        role: newUserData.role,
        createdAt: new Date(),
        updatedAt: new Date()
      });

      // Sign out from secondary auth
      await signOut(secondaryAuth);

      // Refresh user list
      await fetchUsers();

      // Close modal and reset form
      setShowCreateModal(false);
      setNewUserData({ email: '', password: '', role: 'customer' });
      setValidationErrors({});

      showMessage(
        `User ${newUserData.email} created successfully with role: ${newUserData.role}`,
        'success'
      );
    } catch (error) {
      console.error('Error creating user:', error);

      // Handle Firebase errors
      let errorMessage = 'Failed to create user';

      if (error.code === 'auth/email-already-in-use') {
        errorMessage = 'This email is already registered';
        setValidationErrors({ email: errorMessage });
      } else if (error.code === 'auth/invalid-email') {
        errorMessage = 'Invalid email address';
        setValidationErrors({ email: errorMessage });
      } else if (error.code === 'auth/weak-password') {
        errorMessage = 'Password is too weak';
        setValidationErrors({ password: errorMessage });
      } else if (error.code === 'auth/operation-not-allowed') {
        errorMessage = 'Email/password accounts are not enabled';
      } else {
        errorMessage = error.message;
      }

      showMessage(errorMessage, 'error');
    } finally {
      setCreating(false);
    }
  };

  // Handle modal close
  const closeCreateModal = () => {
    setShowCreateModal(false);
    setNewUserData({ email: '', password: '', role: 'customer' });
    setValidationErrors({});
  };

  // Handle input changes
  const handleNewUserChange = (field, value) => {
    setNewUserData(prev => ({ ...prev, [field]: value }));
    // Clear validation error for this field
    if (validationErrors[field]) {
      setValidationErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  useEffect(() => {
    if (userRole === 'admin') {
      fetchUsers();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userRole]);

  // Only admins can access this page
  if (userRole !== 'admin') {
    return <Navigate to="/" replace />;
  }

  const updateUserRole = async (userId, newRole) => {
    try {
      setUpdating(userId);
      await updateDoc(doc(db, 'users', userId), {
        role: newRole,
        updatedAt: new Date()
      });

      // Update local state
      setUsers(users.map(user =>
        user.id === userId ? { ...user, role: newRole } : user
      ));

      showMessage(`Role updated to ${newRole} successfully!`, 'success');
    } catch (error) {
      console.error('Error updating role:', error);
      showMessage('Failed to update role', 'error');
    } finally {
      setUpdating(null);
    }
  };

  const showMessage = (text, type) => {
    setMessage({ text, type });
    setTimeout(() => setMessage({ text: '', type: '' }), 3000);
  };

  const filteredUsers = users.filter(user =>
    user.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.id.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getRoleColor = (role) => {
    switch (role) {
      case 'admin': return '#e74c3c';
      case 'cashier': return '#16a085';
      case 'customer': return '#3498db';
      default: return '#95a5a6';
    }
  };

  const getRoleCount = (role) => {
    return users.filter(u => u.role === role).length;
  };

  if (loading) {
    return (
      <div className="role-management-container">
        <div className="loading">Loading users...</div>
      </div>
    );
  }

  return (
    <div className="role-management-container">
      <div className="role-management-header">
        <h1>User Role Management</h1>
        <p>Manage user roles and permissions</p>
      </div>

      {message.text && (
        <div className={`message ${message.type}`}>
          {message.text}
        </div>
      )}

      <div className="role-stats">
        <div className="stat-card" style={{ borderLeftColor: '#e74c3c' }}>
          <div className="stat-number">{getRoleCount('admin')}</div>
          <div className="stat-label">Admins</div>
        </div>
        <div className="stat-card" style={{ borderLeftColor: '#16a085' }}>
          <div className="stat-number">{getRoleCount('cashier')}</div>
          <div className="stat-label">Cashiers</div>
        </div>
        <div className="stat-card" style={{ borderLeftColor: '#3498db' }}>
          <div className="stat-number">{getRoleCount('customer')}</div>
          <div className="stat-label">Customers</div>
        </div>
        <div className="stat-card" style={{ borderLeftColor: '#95a5a6' }}>
          <div className="stat-number">{users.length}</div>
          <div className="stat-label">Total Users</div>
        </div>
      </div>

      <div className="create-user-section">
        <button
          className="create-user-btn"
          onClick={() => setShowCreateModal(true)}
        >
          <span className="btn-icon">👤</span>
          Create New User
        </button>
      </div>

      <div className="search-bar">
        <input
          type="text"
          placeholder="Search by email or user ID..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="search-input"
        />
      </div>

      <div className="users-table-container">
        <table className="users-table">
          <thead>
            <tr>
              <th>Email</th>
              <th>User ID</th>
              <th>Current Role</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredUsers.length === 0 ? (
              <tr>
                <td colSpan="4" className="no-users">
                  {searchTerm ? 'No users found matching your search' : 'No users found'}
                </td>
              </tr>
            ) : (
              filteredUsers.map((user) => (
                <tr key={user.id}>
                  <td className="user-email">{user.email}</td>
                  <td className="user-id">{user.id.substring(0, 12)}...</td>
                  <td>
                    <span
                      className="role-badge"
                      style={{ backgroundColor: getRoleColor(user.role) }}
                    >
                      {user.role.charAt(0).toUpperCase() + user.role.slice(1)}
                    </span>
                  </td>
                  <td className="actions">
                    <select
                      value={user.role}
                      onChange={(e) => updateUserRole(user.id, e.target.value)}
                      disabled={updating === user.id}
                      className="role-select"
                    >
                      <option value="customer">Customer</option>
                      <option value="cashier">Cashier</option>
                      <option value="admin">Admin</option>
                    </select>
                    {updating === user.id && (
                      <span className="updating-indicator">Updating...</span>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <div className="role-info">
        <h3>Role Descriptions:</h3>
        <ul>
          <li><strong>Customer:</strong> Can place orders and view menu (default role)</li>
          <li><strong>Cashier:</strong> Can process orders, manage transactions, and view reports</li>
          <li><strong>Admin:</strong> Full access to all features including user management and setup</li>
        </ul>
      </div>

      {/* Create User Modal */}
      {showCreateModal && (
        <div className="modal-backdrop" onClick={closeCreateModal}>
          <div className="create-user-modal" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close-btn" onClick={closeCreateModal}>✕</button>

            <h2>Create New User</h2>
            <p className="modal-subtitle">Add a new user to the system</p>

            <div className="create-user-form">
              <div className="form-group">
                <label htmlFor="newUserEmail">Email Address</label>
                <input
                  id="newUserEmail"
                  type="email"
                  value={newUserData.email}
                  onChange={(e) => handleNewUserChange('email', e.target.value)}
                  className={validationErrors.email ? 'input-error' : ''}
                  placeholder="user@example.com"
                  disabled={creating}
                  autoFocus
                />
                {validationErrors.email && (
                  <span className="error-text">{validationErrors.email}</span>
                )}
              </div>

              <div className="form-group">
                <label htmlFor="newUserPassword">Temporary Password</label>
                <input
                  id="newUserPassword"
                  type="password"
                  value={newUserData.password}
                  onChange={(e) => handleNewUserChange('password', e.target.value)}
                  className={validationErrors.password ? 'input-error' : ''}
                  placeholder="Minimum 6 characters"
                  disabled={creating}
                />
                {validationErrors.password && (
                  <span className="error-text">{validationErrors.password}</span>
                )}
                <span className="helper-text">
                  User should change this password after first login
                </span>
              </div>

              <div className="form-group">
                <label htmlFor="newUserRole">User Role</label>
                <select
                  id="newUserRole"
                  value={newUserData.role}
                  onChange={(e) => handleNewUserChange('role', e.target.value)}
                  className="role-select-modal"
                  disabled={creating}
                >
                  <option value="customer">Customer</option>
                  <option value="cashier">Cashier</option>
                  <option value="admin">Admin</option>
                </select>
                <span className="helper-text">
                  {newUserData.role === 'admin' && 'Full system access'}
                  {newUserData.role === 'cashier' && 'Can process orders and view reports'}
                  {newUserData.role === 'customer' && 'Can place orders and view menu'}
                </span>
              </div>

              <div className="modal-actions">
                <button
                  className="cancel-btn"
                  onClick={closeCreateModal}
                  disabled={creating}
                >
                  Cancel
                </button>
                <button
                  className="submit-btn"
                  onClick={createNewUser}
                  disabled={creating}
                >
                  {creating ? 'Creating...' : 'Create User'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserRoleManagement;
